<template>
  <section>
    <output v-show="isDebug"
      >{{ JSON.stringify(middleCerebralArteries) }}
    </output>
    <svg
      viewBox="0 0 535 388"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
    >
      <rect width="535" height="388" fill="url(#pattern0_11_11)" />
      <g
        @click="selected('M1')"
        :class="middleCerebralArteries.includes('M1') ? 'selected' : ''"
      >
        <rect x="128" y="253" width="19" height="14" fill="#D9D9D9" />
        <text
          fill="black"
          xml:space="preserve"
          style="white-space: pre"
          font-family="Sans-Serif"
          font-size="10"
          font-weight="bold"
          letter-spacing="0em"
        >
          <tspan x="130" y="264">M1</tspan>
        </text>
        <line x1="136.5" y1="180" x2="136.5" y2="252" stroke="black" />
      </g>
      <g
        @click="selected('M2')"
        :class="middleCerebralArteries.includes('M2') ? 'selected' : ''"
      >
        <rect x="106" y="253" width="19" height="14" fill="#D9D9D9" />
        <text
          fill="black"
          xml:space="preserve"
          style="white-space: pre"
          font-family="Sans-Serif"
          font-size="10"
          font-weight="bold"
          letter-spacing="0em"
        >
          <tspan x="106" y="264">M2</tspan>
        </text>
        <line x1="116.5" y1="165" x2="116.5" y2="252" stroke="black" />
      </g>
      <g
        @click="selected('M3')"
        :class="middleCerebralArteries.includes('M3') ? 'selected' : ''"
      >
        <rect x="82" y="253" width="19" height="14" fill="#D9D9D9" />
        <text
          fill="black"
          xml:space="preserve"
          style="white-space: pre"
          font-family="Sans-Serif"
          font-size="10"
          font-weight="bold"
          letter-spacing="0em"
        >
          <tspan x="83" y="264">M3</tspan>
        </text>
        <line x1="92" y1="162" x2="92" y2="260" stroke="black" />
      </g>
      <g
        @click="selected('M4')"
        :class="middleCerebralArteries.includes('M4') ? 'selected' : ''"
      >
        <rect x="36" y="68" width="29" height="15" fill="#D9D9D9" />
        <rect x="60" y="253" width="19" height="14" fill="#D9D9D9" />
        <text
          fill="black"
          xml:space="preserve"
          style="white-space: pre"
          font-family="Sans-Serif"
          font-size="10"
          font-weight="bold"
          letter-spacing="0em"
        >
          <tspan x="46" y="78">M4</tspan>
          <tspan x="62" y="264">M4</tspan>
        </text>
        <line x1="63" y1="75.5" x2="117" y2="75.5" stroke="black" />
        <line x1="67.5" y1="208" x2="67.5" y2="260" stroke="black" />
      </g>

      <defs>
        <pattern
          id="pattern0"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use
            xlink:href="#image0_3_54"
            transform="scale(0.000987167 0.00175439)"
          />
        </pattern>
        <image
          id="image0_3_54"
          width="1013"
          height="570"
          :href="middleCerebralArteriesImageUrl"
        />
      </defs>
    </svg>
  </section>
</template>

<script setup lang="ts">
import {
  Contexts,
  CurrentCaseServiceInterface,
  getContainer,
  getContext,
  Helpers,
  NamedBindings,
  ApplicationEvents,
  Version,
  CoreConfiguration,
} from '@dandelion/core';
import { onBeforeMount, ref, Ref } from 'vue';
import { isEqual } from 'lodash';

import middleCerebralArteriesImageUrl from '../../public/images/cerebralArteries.png';

const props = defineProps<{
  context: string;
  fullName: string;
  id: string;
}>();

const coreConfiguration = getContainer(Contexts.DEFAULT).get(
  NamedBindings.CoreConfiguration
) as CoreConfiguration;
const currentCaseService = getContainer(Contexts.DEFAULT).get(
  NamedBindings.CurrentCaseService
) as CurrentCaseServiceInterface;

const middleCerebralArteries = ref<Array<string>>([]);
const isDebug = coreConfiguration.debug;

async function selected(selected: string) {
  const model = (await useCurrentContext(props!.context)).model;

  if (middleCerebralArteries.value.includes(selected)) {
    middleCerebralArteries.value = middleCerebralArteries.value.filter(
      (item: string) => item !== selected
    );
  } else {
    middleCerebralArteries.value.push(selected);
  }
  Helpers.setObjByPath(props.fullName, model, middleCerebralArteries.value);
}

onBeforeMount(async () => {
  const model = (await useCurrentContext(props!.context)).model;
  const middleCerebralArteriesFromModel = Helpers.getObjByPath(
    props.fullName,
    model
  );

  if (!middleCerebralArteries.value) {
    return;
  }
  middleCerebralArteries.value = middleCerebralArteriesFromModel ?? [];
});

async function useCurrentContext(context = Contexts.DEFAULT) {
  let crfVersions: Readonly<Ref<ReadonlyMap<string, Version>>> = ref(new Map());
  try {
    crfVersions = ref(currentCaseService.getCrfVersions());
  } catch (e) {
    // TODO: maybe remove error thrown, there are cases when no version is available
    // e.g. the context is not set, no current patient etc.
  }
  const model = await currentCaseService.getModel();
  const keyForReRender = ref(0);

  getContext(context).addEventListener(
    ApplicationEvents.CaseOpenedForEditing,
    async () => {
      const currentCrfVersions = currentCaseService.getCrfVersions();
      if (!isEqual(currentCrfVersions, crfVersions)) {
        crfVersions = currentCrfVersions;
      }
      keyForReRender.value = keyForReRender.value + 1;
    }
  );
  return { keyForReRender, crfVersions, model };
}
</script>

<style scoped>
g:hover rect {
  stroke: #4030ff !important;
  cursor: pointer;
}

g:hover text {
  cursor: pointer;
}

.selected rect {
  fill: red;
}

.selected line {
  stroke: red;
}

.selected path {
  stroke: red;
}
</style>
